/**
 * Slots page — 9 Venture Slots (контракты стейкинга).
 *
 * Структура:
 *   - Hero summary card: сколько сейчас застейкано, сколько активных
 *     слотов, лучший APR, total earned
 *   - Scrollable список 9 VentureSlotInfo с SlotCard'ами
 *   - Month header каждые 3 слота (Месяц 1 / 2 / 3)
 *   - Tap на unlocked → SlotDepositModal
 *   - Tap на active → SlotDetailsModal (позже)
 *
 * Стиль — iOS Liquid Glass, тот же что и Dashboard.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, type Variants } from 'motion/react';
import { Sparkles, TrendingUp, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GlassSurface, GlassPill, LiquidNumber } from '@/components/glass';
import {
  SlotCard,
  type ActiveSlotInstance,
} from '@/components/slots/SlotCard';
import { SlotDepositModal } from '@/components/slots/SlotDepositModal';
import { SlotDetailsModal } from '@/components/slots/SlotDetailsModal';
import { PlasmaCorePurchaseModal } from '@/components/slots/PlasmaCorePurchaseModal';
import { VENTURE_SLOTS, type VentureSlotInfo } from '@/data/ventureSlots';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useAuth } from '@/contexts/AuthContext';
import { lunaApi } from '@/services/api';
import heroConstellation from '@/assets/slot-decor/hero.webp';
import badgeM1 from '@/assets/slot-decor/badge-m1.webp';
import badgeM2 from '@/assets/slot-decor/badge-m2.webp';
import badgeM3 from '@/assets/slot-decor/badge-m3.webp';

const MONTH_BADGES: Record<1 | 2 | 3, string> = {
  1: badgeM1,
  2: badgeM2,
  3: badgeM3,
};

// Mock user portfolio — will come from GET /api/luna/slots/my.
// Multiple instances of the same slot type are now supported: a
// user can open three $100 Photon contracts and each lives as its
// own row here.
interface OwnedInstance {
  slotId: string;
  instance: ActiveSlotInstance;
}

// Fallback mock (пока API не ответит)
const EMPTY_OWNED: OwnedInstance[] = [];

// Доступность нод теперь приходит с API /slots/config — бэк сам считает
// по `launch_date`. Хранится мапа dbId → { available, unlockAtMsk }.
interface SlotApiState {
  available: boolean;
  unlockAtMsk?: string;
  unlockAt?: string;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { delayChildren: 0.05, staggerChildren: 0.06 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.32, 0.72, 0, 1] },
  },
};

export function Slots() {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const navigate = useNavigate();
  const { user: authUser, refresh: refreshAuth } = useAuth();
  const [depositSlot, setDepositSlot] = useState<VentureSlotInfo | null>(null);
  const [detailsSlot, setDetailsSlot] = useState<VentureSlotInfo | null>(null);
  const [pcTarget, setPcTarget] = useState<{
    contractIds: string[];
    slot: VentureSlotInfo;
  } | null>(null);
  const [ownedSlots, setOwnedSlots] = useState<OwnedInstance[]>(EMPTY_OWNED);
  const [archiveCount, setArchiveCount] = useState(0);
  const [slotsApi, setSlotsApi] = useState<Map<number, SlotApiState>>(
    () => new Map(),
  );

  // Загружаем актуальное состояние нод (бэк сам считает по launch_date).
  useEffect(() => {
    lunaApi.slots.config().then((res) => {
      const d = res.data as {
        success: boolean;
        configs?: Array<{
          id: number;
          available: boolean;
          unlockAt?: string;
          unlockAtMsk?: string;
        }>;
      };
      if (d.success && d.configs) {
        const next = new Map<number, SlotApiState>();
        for (const c of d.configs) {
          next.set(c.id, {
            available: c.available,
            unlockAt: c.unlockAt,
            unlockAtMsk: c.unlockAtMsk,
          });
        }
        setSlotsApi(next);
      }
    }).catch(() => { /* fallback: считаем что всё открыто */ });
  }, []);

  // Загружаем реальные контракты юзера
  const fetchOwned = () => {
    lunaApi.slots.my().then((res) => {
      const d = res.data as { success: boolean; contracts?: Array<{
        id: string; slotName: string; totalPaidOut: string;
        createdAt: string; hasPlasmaCore: boolean; status: string;
      }> };
      if (d.success && d.contracts) {
        const active = d.contracts.filter((c) => c.status === 'active');
        setArchiveCount(d.contracts.filter((c) => c.status === 'burned').length);
        setOwnedSlots(active.map((c) => {
          const daysActive = Math.max(1, Math.round(
            (Date.now() - new Date(c.createdAt).getTime()) / 86_400_000,
          ));
          return {
            slotId: c.slotName.toLowerCase(),
            instance: {
              contractId: c.id,
              earned: parseFloat(c.totalPaidOut),
              daysActive,
              plasmaCoreActive: c.hasPlasmaCore,
            },
          };
        }));
      }
    }).catch(() => { /* fallback пустой */ });
  };

  useEffect(() => { fetchOwned(); }, []);

  // Group owned instances by slot id for O(1) lookup
  const ownedByType = useMemo(() => {
    const map = new Map<string, ActiveSlotInstance[]>();
    for (const row of ownedSlots) {
      const list = map.get(row.slotId) ?? [];
      list.push(row.instance);
      map.set(row.slotId, list);
    }
    return map;
  }, [ownedSlots]);

  // Top-level summary stats — пересчитывается при изменении ownedSlots
  const { totalStaked, totalEarned, bestMonthly, activeCount } = useMemo(() => {
    let staked = 0;
    let earned = 0;
    let best = 0;
    for (const row of ownedSlots) {
      const slot = VENTURE_SLOTS.find((s) => s.id === row.slotId);
      if (!slot) continue;
      staked += slot.amount;
      earned += row.instance.earned;
      if (slot.monthlyRate > best) best = slot.monthlyRate;
    }
    return {
      totalStaked: staked,
      totalEarned: earned,
      bestMonthly: best,
      activeCount: ownedSlots.length,
    };
  }, [ownedSlots]);

  // Group slots by unlockMonth for the section headers
  const byMonth = useMemo(() => {
    const m1 = VENTURE_SLOTS.filter((s) => s.unlockMonth === 1);
    const m2 = VENTURE_SLOTS.filter((s) => s.unlockMonth === 2);
    const m3 = VENTURE_SLOTS.filter((s) => s.unlockMonth === 3);
    return { m1, m2, m3 };
  }, []);

  const handleTapDetails = (slot: VentureSlotInfo) => {
    haptic.light();
    navigate(`/slots/${slot.id}`);
  };

  const handleTapBuy = (slot: VentureSlotInfo) => {
    haptic.medium();
    setDepositSlot(slot);
  };

  const handleTapPC = (slot: VentureSlotInfo, contractIds: string[]) => {
    haptic.medium();
    setPcTarget({ contractIds, slot });
  };

  return (
    <>
      <motion.div
        className="space-y-5 max-w-md mx-auto pt-2 pb-6"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {/* ─── HERO: portfolio summary ─── */}
        <motion.div variants={itemVariants}>
          <GlassSurface variant="strong" className="p-6 relative overflow-hidden">
            {/* Decorative constellation — watermark in top-right corner */}
            <img
              src={heroConstellation}
              alt=""
              aria-hidden
              className="absolute pointer-events-none select-none"
              style={{
                top: -36,
                right: -36,
                width: 200,
                height: 200,
                opacity: 0.18,
                mixBlendMode: 'screen',
              }}
            />
            <div className="relative flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1 font-mono">
                  {t('slots.fleet')}
                </p>
                <h1 className="font-display text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight">
                  {t('slots.title')}
                </h1>
              </div>
              <GlassPill tone="lunar" dot compact>
                {activeCount}
              </GlassPill>
            </div>

            {/* Total staked */}
            <div className="mb-5">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-1 font-mono">
                {t('slots.capital')}
              </p>
              <div className="flex items-baseline gap-1.5">
                <LiquidNumber
                  value={totalStaked}
                  decimals={0}
                  suffix="USD₮"
                  className="text-[36px] font-extrabold font-display text-[var(--text-primary)] tracking-tight"
                />
              </div>
              <p className="text-[11px] text-[var(--text-secondary)] mt-1">
                {t('slots.incomeDistributed', { earned: totalEarned > 0 ? parseFloat(totalEarned.toFixed(3)) : 0 })}
              </p>
            </div>

            {/* 3 mini stats */}
            <div className="grid grid-cols-3 gap-2.5">
              <MiniStatCell
                icon={<Layers size={11} strokeWidth={2.4} />}
                label={t('slots.working')}
                value={`${activeCount}`}
                bigValue
              />
              <MiniStatCell
                icon={<TrendingUp size={11} strokeWidth={2.4} />}
                label={t('slots.roi')}
                value={`~0,01–${bestMonthly}%`}
                accent
                bigValue
              />
              <MiniStatCell
                icon={<Sparkles size={11} strokeWidth={2.4} />}
                label={t('slots.earned')}
                value={`${totalEarned.toFixed(0)} USD₮`}
                bigValue
              />
            </div>
          </GlassSurface>
        </motion.div>

        {/* ─── МЕСЯЦ 1 ─── */}
        <MonthSection
          variants={itemVariants}
          month={1}
          title={t('slots.monthName1')}
        >
          {byMonth.m1.map((slot) => {
            const apiState = slotsApi.get(slot.dbId);
            const unlocked = apiState?.available ?? true;
            const owned = ownedByType.get(slot.id) ?? [];
            return (
              <SlotCard
                key={slot.id}
                slot={slot}
                unlocked={unlocked}
                owned={owned}
                onTapDetails={() => handleTapDetails(slot)}
                onTapBuy={() => handleTapBuy(slot)}
                onTapPC={(ids) => handleTapPC(slot, ids)}
              />
            );
          })}
        </MonthSection>

        {/* ─── МЕСЯЦ 2 — серые + countdown до даты ─── */}
        <MonthSection
          variants={itemVariants}
          month={2}
          title={t('slots.monthName2')}
        >
          {byMonth.m2.map((slot) => {
            const apiState = slotsApi.get(slot.dbId);
            const unlocked = apiState?.available ?? false;
            const owned = ownedByType.get(slot.id) ?? [];
            return (
              <SlotCard
                key={slot.id}
                slot={slot}
                unlocked={unlocked}
                owned={owned}
                unlockAt={apiState?.unlockAt}
                unlockAtMsk={apiState?.unlockAtMsk}
                showCountdown
                onTapDetails={() => handleTapDetails(slot)}
                onTapBuy={() => handleTapBuy(slot)}
                onTapPC={(ids) => handleTapPC(slot, ids)}
              />
            );
          })}
        </MonthSection>

        {/* ─── МЕСЯЦ 3 — приглушённые «Ожидайте» без счётчика ─── */}
        <MonthSection
          variants={itemVariants}
          month={3}
          title={t('slots.monthName3')}
          highlight
        >
          {byMonth.m3.map((slot) => {
            const apiState = slotsApi.get(slot.dbId);
            const unlocked = apiState?.available ?? false;
            const owned = ownedByType.get(slot.id) ?? [];
            return (
              <SlotCard
                key={slot.id}
                slot={slot}
                unlocked={unlocked}
                owned={owned}
                unlockAtMsk={apiState?.unlockAtMsk}
                onTapDetails={() => handleTapDetails(slot)}
                onTapBuy={() => handleTapBuy(slot)}
                onTapPC={(ids) => handleTapPC(slot, ids)}
              />
            );
          })}
        </MonthSection>

        {/* Архив отработавших нод — ссылка внизу страницы. Не мозолит
            глаза в активном пользовании, но всегда доступна прокруткой. */}
        {archiveCount > 0 && (
          <motion.div variants={itemVariants} className="pt-2">
            <button
              type="button"
              onClick={() => { haptic.light(); navigate('/slots/archive'); }}
              className="w-full rounded-2xl px-5 py-3.5 flex items-center justify-between active:opacity-70 transition-opacity"
              style={{
                background: 'var(--glass-fill)',
                border: '1px solid var(--glass-stroke)',
                boxShadow: 'var(--glass-shadow-soft)',
              }}
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-bold">
                {t('archive.linkLabel')} · {archiveCount}
              </span>
              <span className="text-[var(--text-muted)] text-[16px]">›</span>
            </button>
          </motion.div>
        )}
      </motion.div>

      <SlotDepositModal
        slot={depositSlot}
        onClose={() => setDepositSlot(null)}
        onConfirm={() => {
          haptic.success();
          setDepositSlot(null);
          fetchOwned();     // Перезагружаем список контрактов
          void refreshAuth(); // Обновляем баланс в хедере
        }}
      />

      <SlotDetailsModal
        slot={detailsSlot}
        instances={detailsSlot ? (ownedByType.get(detailsSlot.id) ?? []) : []}
        onClose={() => setDetailsSlot(null)}
        onBuyMore={() => {
          const s = detailsSlot;
          setDetailsSlot(null);
          if (s) setDepositSlot(s);
        }}
      />

      <PlasmaCorePurchaseModal
        open={pcTarget !== null}
        contractIds={pcTarget?.contractIds ?? []}
        slotName={pcTarget?.slot.name ?? ''}
        slotPrice={pcTarget?.slot.amount ?? 0}
        userBalance={parseFloat(authUser?.usdtBalance ?? '0')}
        onClose={() => setPcTarget(null)}
        onSuccess={() => {
          setPcTarget(null);
          fetchOwned();
          void refreshAuth();
        }}
      />
    </>
  );
}

// ─── Sub components ──────────────────────────────────────────────

function MonthSection({
  variants,
  month,
  title,
  highlight,
  children,
}: {
  variants: Variants;
  month: 1 | 2 | 3;
  title: string;
  highlight?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const accent = highlight ? 'var(--accent-primary)' : 'var(--text-muted)';
  return (
    <motion.section variants={variants} className="space-y-3">
      <div className="flex items-center gap-3 px-1">
        {/* Generated badge icon — month-specific glyph */}
        <div
          className="flex items-center justify-center flex-shrink-0 rounded-[14px]"
          style={{
            width: 36,
            height: 36,
            background: highlight
              ? 'color-mix(in oklab, var(--accent-primary) 18%, transparent)'
              : 'var(--glass-fill)',
            border: highlight
              ? '1px solid color-mix(in oklab, var(--accent-primary) 45%, transparent)'
              : '1px solid var(--glass-stroke)',
            boxShadow: highlight
              ? '0 0 22px -4px color-mix(in oklab, var(--accent-primary) 55%, transparent)'
              : undefined,
          }}
        >
          <img
            src={MONTH_BADGES[month]}
            alt={`Month ${month}`}
            width={24}
            height={24}
            style={{
              filter: highlight
                ? 'drop-shadow(0 0 6px color-mix(in oklab, var(--accent-primary) 70%, transparent))'
                : 'drop-shadow(0 0 4px rgba(255,255,255,0.18))',
            }}
          />
        </div>
        <div className="flex flex-col leading-tight">
          <span
            className="font-mono text-[9px] uppercase tracking-[0.2em] font-extrabold"
            style={{ color: accent }}
          >
            {t('slots.month', { n: month })}
          </span>
          <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-mono">
            {title}
          </span>
        </div>
      </div>
      {children}
    </motion.section>
  );
}

function MiniStatCell({
  icon,
  label,
  value,
  accent,
  bigValue,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: boolean;
  bigValue?: boolean;
}) {
  return (
    <div className={`lg-pill !rounded-2xl px-3 py-2.5 flex flex-col ${bigValue ? 'items-center justify-center gap-1.5 text-center' : 'gap-1'}`}>
      <span className="flex items-center gap-1 text-[8.5px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-mono font-bold whitespace-nowrap">
        {icon}
        {label}
      </span>
      <span
        className={`font-mono font-extrabold tabular-nums whitespace-nowrap ${
          bigValue ? 'text-[15px]' : 'text-[12px]'
        } ${accent ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'}`}
      >
        {value}
      </span>
    </div>
  );
}
